import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const cx = (...c: (string | false | null | undefined)[]) =>
  c.filter(Boolean).join(" ");

export type TreeTone = "ok" | "warning" | "alarm" | "neutral";

export interface TreeNode {
  id: string;
  label: React.ReactNode;
  /** Optional status: colored dot + right-aligned label. */
  status?: { tone: TreeTone; label?: string };
  /** Optional leading icon (overrides the status dot when provided). */
  icon?: React.ReactNode;
  children?: TreeNode[];
  /** Arbitrary payload returned by onSelect. */
  data?: unknown;
}

const TONE_DOT: Record<TreeTone, string> = {
  ok: "bg-emerald-500",
  warning: "bg-amber-500",
  alarm: "bg-red-500",
  neutral: "bg-gray-300 dark:bg-gray-600",
};

const TONE_TEXT: Record<TreeTone, string> = {
  ok: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  alarm: "text-red-600 dark:text-red-400",
  neutral: "text-gray-400 dark:text-gray-500",
};

interface TreeRowProps {
  node: TreeNode;
  depth: number;
  collapsed: Set<string>;
  toggle: (id: string) => void;
  selectedId?: string;
  onSelect?: (node: TreeNode) => void;
}

const TreeRow: React.FC<TreeRowProps> = ({
  node,
  depth,
  collapsed,
  toggle,
  selectedId,
  onSelect,
}) => {
  const hasChildren = !!node.children && node.children.length > 0;
  const isOpen = hasChildren && !collapsed.has(node.id);
  const isSelected = selectedId === node.id;

  return (
    <div>
      <button
        type="button"
        onClick={() => {
          if (hasChildren) toggle(node.id);
          onSelect?.(node);
        }}
        className={cx(
          "group w-full flex items-center gap-2 rounded-lg py-1.5 pr-2.5 text-sm transition-colors",
          isSelected
            ? "bg-blue-50 dark:bg-blue-900/30"
            : "hover:bg-gray-100 dark:hover:bg-gray-700/50",
        )}
        style={{ paddingLeft: depth * 18 + 8 }}
      >
        {/* Chevron (or spacer for leaves) */}
        {hasChildren ? (
          <motion.svg
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.18 }}
            className="w-3.5 h-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M9 5l7 7-7 7"
            />
          </motion.svg>
        ) : (
          <span className="w-3.5 flex-shrink-0" />
        )}

        {/* Leading icon or status dot */}
        {node.icon ? (
          <span className="flex-shrink-0">{node.icon}</span>
        ) : node.status ? (
          <span
            className={cx(
              "w-2 h-2 rounded-full flex-shrink-0",
              TONE_DOT[node.status.tone],
              node.status.tone === "alarm" && "animate-pulse",
            )}
          />
        ) : null}

        <span
          className={cx(
            "truncate",
            isSelected
              ? "font-semibold text-blue-700 dark:text-blue-300"
              : hasChildren
                ? "font-medium text-gray-800 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-300",
          )}
        >
          {node.label}
        </span>

        {node.status?.label && (
          <span
            className={cx(
              "ml-auto text-xs font-medium flex-shrink-0",
              TONE_TEXT[node.status.tone],
            )}
          >
            {node.status.label}
          </span>
        )}
      </button>

      <AnimatePresence initial={false}>
        {hasChildren && isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {node.children!.map((child) => (
              <TreeRow
                key={child.id}
                node={child}
                depth={depth + 1}
                collapsed={collapsed}
                toggle={toggle}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface TreeViewProps {
  nodes: TreeNode[];
  selectedId?: string;
  onSelect?: (node: TreeNode) => void;
  className?: string;
  /** Show an "Expand all / Collapse all" toolbar above the tree. */
  showControls?: boolean;
}

/** Collect ids of every node that has children (i.e. is collapsible). */
function collectParentIds(nodes: TreeNode[], acc: string[] = []): string[] {
  for (const n of nodes) {
    if (n.children && n.children.length > 0) {
      acc.push(n.id);
      collectParentIds(n.children, acc);
    }
  }
  return acc;
}

/**
 * Modern, theme-aware, animated tree view. Nodes are expanded by default
 * (including any that appear later from live data); clicking a parent toggles
 * it. Leaves report clicks through `onSelect`.
 */
const TreeView: React.FC<TreeViewProps> = ({
  nodes,
  selectedId,
  onSelect,
  className,
  showControls,
}) => {
  // Track only what's been explicitly collapsed so live-added groups stay open.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const parentIds = useMemo(() => collectParentIds(nodes), [nodes]);
  const allCollapsed =
    parentIds.length > 0 && parentIds.every((id) => collapsed.has(id));
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(parentIds));

  return (
    <div className={cx("select-none", className)}>
      {showControls && parentIds.length > 0 && (
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={allCollapsed ? expandAll : collapseAll}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={allCollapsed ? "M19 9l-7 7-7-7" : "M5 15l7-7 7 7"}
              />
            </svg>
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        </div>
      )}
      {nodes.map((node) => (
        <TreeRow
          key={node.id}
          node={node}
          depth={0}
          collapsed={collapsed}
          toggle={toggle}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

export default TreeView;
